import { CampProps, SiteProps } from "../../../../../interfaces"
import { IFormData, ILineItemField } from "../../EditInvoice"
import React, { useEffect, useState } from "react"
import useAdminForm from "../../../../../hooks/useAdminForm"
import { LINE_ITEM_KINDS, lineItemKind } from "~/constants/line_item_kinds"
import { formatMoney } from "~/utils/formatMoney"
import Form from "../../../../ui/Admin/Form"
import { ArrowPathRoundedSquareIcon } from "@heroicons/react/24/outline"
import classNames from "../../../../../utils/classNames"
import { TrashIcon } from "@heroicons/react/24/solid"
import IAddOn from "../../../../../interfaces/IAddOn"
import ProductDropdown from "../../../Products/ProductDropdown"
import { currencySymbol } from "~/utils/currencySymbol"
import HoverTip from "~/components/ui/HoverTip"

interface LineItemRowProps {
  camp: CampProps
  item: ILineItemField
  index: number
  isPreview: boolean
  site: SiteProps
  isRecurring: boolean
  isForPrint?: boolean
}

const LineItemRow = ({ camp, item, index, isPreview, site, isRecurring, isForPrint }: LineItemRowProps) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const { resource, watch, setValue, isChanged } = useAdminForm<IFormData>()
  const items = watch("items")
  const totalPrice = watch(`items.${index}.total_amount`)
  const unitPrice = watch(`items.${index}.unit_amount`)
  const occurrences = watch(`items.${index}.occurrences`)
  const quantity = watch(`items.${index}.quantity`)
  const label = watch(`items.${index}.label`)
  const kind = watch(`items.${index}.kind`)
  const is_updating_series = watch(`items.${index}.is_updating_series`)

  const onChangeProduct = (product: IAddOn) => {
    setValue(`items.${index}.label`, product.label)
    setValue(`items.${index}.unit_amount`, product.amount_in_cents / 100)
    setValue(`items.${index}.add_on_id`, product.id)
    setValue(`items.${index}.quantity`, 1)
    setValue(`items.${index}.total_amount`, product.amount_in_cents / 100)
    setValue(`items.${index}.kind`, LINE_ITEM_KINDS.BASIC)
  }

  const onCreateProduct = (label: string) => {
    setValue(`items.${index}.label`, label)
    setValue(`items.${index}.kind`, LINE_ITEM_KINDS.CUSTOM)

    setValue(`items.${index}.add_on_id`, "")
  }

  const isItemDeletable = () => {
    const unremovableKinds = [
      LINE_ITEM_KINDS.HOLIDAY_RATE,
      "weekend_rate",
      "additional_adults",
      "additional_kids",
      "additional_pets",
    ] as lineItemKind[]
    if (unremovableKinds.includes(item.kind)) return false
    return !item.reservation_id
  }

  useEffect(() => {
    const watchedItem = watch(`items.${index}`) as ILineItemField
    if (item?.reservation_id || watchedItem?.reservation_id) {
      return
    }

    setValue(`items.${index}.total_amount`, unitPrice * quantity)
  }, [unitPrice, quantity])

  useEffect(() => {
    if (item?.reservation_id) {
      const isReservationLineItem = !item.add_on_id && item.kind === "basic" && !item.parent_line_item_id
      const hasOccurrences = !!item.occurrences && item.occurrence_unit !== "months" && !isReservationLineItem
      let newTotal = item.total_amount / item.quantity

      if (hasOccurrences) newTotal = newTotal / item.occurrences

      setValue(`items.${index}.unit_amount`, newTotal)
    }
  }, [totalPrice])

  const onDeleteItem = () => {
    setValue(
      "items",
      items.filter((_, i) => i !== index),
    )
  }

  const onUpdateIsUpdatingSeries = () => {
    setValue(`items.${index}.is_updating_series`, !item.is_updating_series)
  }

  const quantityForMobile = resource.is_displaying_occurrences ? items[index].occurrences : items[index].quantity

  if (item.is_hidden || (item.unit_amount === 0 && isPreview)) {
    return <></>
  }

  if (isPreview) {
    return (
      <div
        className={classNames(
          resource.is_displaying_occurrences
            ? classNames("grid-cols-2", isForPrint ? "custom-500:grid-cols-10" : "custom-500:grid-cols-8")
            : "grid-cols-2 custom-500:grid-cols-8",
          "grid md:grid-cols-12 gap-2 mt-4 border-b border-gray-200 pb-4 w-full",
        )}
      >
        <div
          className={
            resource.is_displaying_occurrences
              ? "col-span-1 custom-500:col-span-2 md:col-span-4"
              : "col-span-1 custom-500:col-span-2 md:col-span-5"
          }
        >
          {label}
        </div>
        <div className="hidden custom-500:block col-span-2">{formatMoney(unitPrice, camp?.currency)}</div>
        {resource.is_displaying_occurrences && (
          <div className={classNames(!isForPrint && "hidden", "col-span-1", isPreview ? "md:block" : "custom-500:block")}>{occurrences || "-"}</div>
        )}
        <div className="hidden custom-500:block col-span-2">{quantity || "-"}</div>
        <div className="custom-500:flex flex-col col-span-1 custom-500:col-span-2">
          <div className="">{totalPrice ? formatMoney(totalPrice, camp?.currency) : "-"}</div>
          <div className="custom-500:hidden">
            {quantityForMobile > 1 ? `(${quantityForMobile} * ${items[index].unit_amount}$)` : <></>}
          </div>
        </div>
      </div>
    )
  } else {
    if (item) {
      const isLabelReadOnly = item.kind === LINE_ITEM_KINDS.ELECTRIC
      const isUnitPriceReadOnly = !!item.reservation_id
      const isQuantityReadOnly = !!item.reservation_id
      const isTotalReadOnly = !item.reservation_id

      const isRowChanged =
        isChanged(`items.${index}.total_amount`) ||
        isChanged(`items.${index}.unit_amount`) ||
        isChanged(`items.${index}.occurrences`) ||
        isChanged(`items.${index}.quantity`) ||
        isChanged(`items.${index}.label`) ||
        isChanged(`items.${index}.kind`)


      return (
        <div
          className={classNames(
            resource.is_displaying_occurrences ? "grid-cols-10" : "grid-cols-8 custom-500:grid-cols-9",
            "grid md:grid-cols-12 gap-2 mb-2",
          )}
        >
          {isLabelReadOnly ? (
            <div
              className={classNames(
                "flex items-center",
                resource.is_displaying_occurrences ? "col-span-2 md:col-span-4" : "col-span-2 md:col-span-5",
              )}
            >
              {watch(`items.${index}.label`)}
            </div>
          ) : item.add_on_id ? (
            <div
              className={classNames(
                "flex items-center",
                resource.is_displaying_occurrences ? "col-span-2 md:col-span-4" : "col-span-2 md:col-span-5",
              )}
            >
              <HoverTip
                content="Product labels cannot be changed - if you'd like to update the product you can do so in the settings"
                active={item.add_on_id}
                width="w-96"
              >
                <>{watch(`items.${index}.label`)}</>
              </HoverTip>
            </div>
          ) : item.reservation_id?.length ? (
            <Form.TextInput
              field={`items.${index}.label`}
              className={resource.is_displaying_occurrences ? "col-span-2 md:col-span-4" : "col-span-2 md:col-span-5"}
              asText={isLabelReadOnly}
            />
          ) : (
            <div
              className={resource.is_displaying_occurrences ? "col-span-2 md:col-span-4" : "col-span-2 md:col-span-5"}
            >
              <ProductDropdown
                site={site}
                disabledProductIds={watch("items").map((li) => li.add_on_id)}
                onChange={onChangeProduct}
                createNewItem={onCreateProduct}
                selectedId={watch(`items.${index}`).add_on_id}
                lineItem={watch(`items.${index}`)}
                menuPosition="fixed"
                dataTestId={`line-item-product-dropdown-${index}`}
              />
            </div>
          )}
          <Form.DecimalInput
            field={`items.${index}.unit_amount`}
            prefix={currencySymbol(camp?.currency) || "$"}
            suffix={camp.currency.toUpperCase()}
            className="col-span-2 block"
            asText={isUnitPriceReadOnly}
            min={-9999999}
            max={9999999}
          />
          {resource.is_displaying_occurrences && (
            <Form.NumberInput field={`items.${index}.occurrences`} className="col-span-1" asText />
          )}
          <Form.NumberInput
            isIntegerOnly={item.kind !== LINE_ITEM_KINDS.ELECTRIC}
            field={`items.${index}.quantity`}
            className="col-span-2"
            asText={isQuantityReadOnly}
          />
          <Form.DecimalInput
            field={`items.${index}.total_amount`}
            prefix={currencySymbol(camp?.currency) || "$"}
            suffix={camp.currency.toUpperCase()}
            className={classNames(resource.is_displaying_occurrences ? "col-span-2" : "custom-500:col-span-2")}
            asText={isTotalReadOnly}
            min={-9999999}
            max={9999999}
          />
          <div className="col-span-1 flex justify-center items-center" style={{ paddingBottom: 7 }}>
            {!item.reservation_id &&
              [LINE_ITEM_KINDS.CUSTOM, LINE_ITEM_KINDS.BASIC].includes(item.kind) &&
              isRecurring && (
                <div className="relative mr-auto flex items-center justify-center">
                  <button
                    disabled={!isRowChanged}
                    onClick={onUpdateIsUpdatingSeries}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    className={classNames(
                      "transition-colors duration-100 rounded-md p-2",
                      (is_updating_series && "bg-green-600") || "",
                      (!isRowChanged && "opacity-30 pointer-events-none") || "",
                    )}
                  >
                    <ArrowPathRoundedSquareIcon
                      className={classNames(
                        "w-5.5 h-5.5 p-0 transition-colors duration-100",
                        is_updating_series ? "text-white stroke-2" : "text-gray-500",
                      )}
                    />
                  </button>
                  {showTooltip && (
                    <div
                      className="absolute z-20 bottom-full left-0 mb-2 flex items-center"
                      style={{ transform: "translate(-80%, -0px)" }}
                    >
                      <div className="relative w-max bg-white text-gray-500 text-sm shadow-md rounded-md px-4 py-1">
                        <p>
                          {isRowChanged
                            ? is_updating_series
                              ? "Applying changes to future recurrences"
                              : "Apply changes to future recurrences"
                            : "No changes to apply to future recurrences of this reservation"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

            {isItemDeletable() && <TrashIcon onClick={onDeleteItem} className="ml-auto w-6 h-6 cursor-pointer" />}
          </div>
        </div>
      )
    } else return <></>
  }
}

export default LineItemRow
